import React, { useState } from 'react';
import { FaArrowLeft, FaSatelliteDish, FaRocket, FaGlobe, FaSatellite, FaChartLine, FaUsers, FaCog, FaShieldAlt, FaDatabase, FaMicrochip, FaEye, FaWifi, FaMapMarkedAlt } from 'react-icons/fa';

const SpaceTechnologiesPage = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [

    {
      id: 1,
      name: "NextGen Satellite Constellation",
      status: "Active Development",
      timeline: "2024-2026",
      description: "Deploying a network of 500 low-Earth orbit satellites for global broadband connectivity",
      technologies: ["Advanced Propulsion", "AI Navigation", "Solar Panel Arrays", "Quantum Communication"],
      budget: "$2.5B",
      team: 150,
      phase: "Phase 2: Prototype Testing"
    },

    {
      id: 2,
      name: "Mars Communication Relay",
      status: "Research Phase",
      timeline: "2025-2028",
      description: "Establishing reliable communication infrastructure between Earth and Mars colonies",
      technologies: ["Deep Space Antennas", "Signal Processing", "Orbital Mechanics", "Radiation Hardening"],
      budget: "$800M",
      team: 85,
      phase: "Phase 1: Feasibility Study"
      
    },

    {
      id: 3,
      name: "Orbital Manufacturing Platform",
      status: "Conceptual",
      timeline: "2026-2030",
      description: "Zero-gravity manufacturing facility for advanced materials and pharmaceuticals",
      technologies: ["Robotic Systems", "3D Printing", "Material Science", "Life Support Systems"],
      budget: "$1.2B",
      team: 65,
      phase: "Phase 0: Concept Development"
    },
    {
      id: 4,
      name: "Earth Observation Network",
      status: "Operational",
      timeline: "2022-2025",
      description: "Real-time Earth monitoring for climate research and disaster response",
      technologies: ["Hyperspectral Imaging", "Machine Learning", "Data Analytics", "Cloud Computing"],
      budget: "$450M",
      team: 120,
      phase: "Phase 3: Full Operation"
    }

  ];

  const capabilities = [
    {
      icon: <FaSatellite />,
      title: "Satellite Design & Manufacturing",
      description: "Custom satellite solutions from CubeSats to large-scale communication platforms"
    },
    {
      icon: <FaRocket />,
      title: "Launch Services Integration",
      description: "End-to-end launch coordination with major space agencies and private companies"
    },
    {
      icon: <FaDatabase />,
      title: "Space Data Analytics",
      description: "Advanced processing and analysis of satellite-generated data for actionable insights"
    },
    {
      icon: <FaMicrochip />,
      title: "Space-Grade Electronics",
      description: "Radiation-hardened computing systems designed for harsh space environments"
    },
    {
      icon: <FaShieldAlt />,
      title: "Mission Control Systems",
      description: "24/7 satellite operations and monitoring from our state-of-the-art control center"
    },
    {
      icon: <FaWifi />,
      title: "Communication Solutions",
      description: "High-bandwidth space-to-ground and inter-satellite communication systems"
    }
  ];

  const achievements = [
    { metric: "50+", label: "Satellites Deployed" },
    { metric: "15", label: "Countries Served" },
    { metric: "99.7%", label: "Mission Success Rate" },
    { metric: "24/7", label: "Operations Support" }
  ];

  const renderOverview = () => (
    <div className="space-overview">
      <div className="company-intro">
        <h2>Leading the Future of Space Technology</h2>
        <p className="intro-text">
          Our company stands at the forefront of space innovation, developing cutting-edge technologies 
          that enable humanity's expansion beyond Earth. From advanced satellite systems to deep space 
          communication networks, we're building the infrastructure for tomorrow's space economy.
        </p>
      </div>

      <div className="achievements-grid">
        {achievements.map((achievement, index) => (
          <div key={index} className="achievement-card">
            <div className="achievement-metric">{achievement.metric}</div>
            <div className="achievement-label">{achievement.label}</div>
          </div>
        ))}
      </div>

      <div className="capabilities-section">
        <h3>Core Capabilities</h3>
        <div className="capabilities-grid">
          {capabilities.map((capability, index) => (
            <div key={index} className="capability-card">
              <div className="capability-icon">{capability.icon}</div>
              <h4>{capability.title}</h4>
              <p>{capability.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="projects-section mx-3">
      <h2>Current Projects & Initiatives</h2>
      <div className="projects-grid">
        {projects.map((project) => (
          <div 
            key={project.id} 
            className={`project-card ${selectedProject?.id === project.id ? 'selected' : ''}`}
            onClick={() => setSelectedProject(project)}
          >
            <div className="project-header">
              <h3>{project.name}</h3>
              <span className={`status-badge ${project.status.toLowerCase().replace(' ', '-')}`}>
                {project.status}
              </span>
            </div>
            <p className="project-description">{project.description}</p>
            <div className="project-meta">
              <div className="meta-item">
                <FaChartLine />
                <span>{project.timeline}</span>
              </div>
              <div className="meta-item">
                <FaUsers />
                <span>{project.team} team members</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedProject && (
        <div className="project-details">
          <h3>Project Details: {selectedProject.name}</h3>
          <div className="details-grid">
            <div className="detail-section">
              <h4>Current Phase</h4>
              <p>{selectedProject.phase}</p>
            </div>
            <div className="detail-section">
              <h4>Budget</h4>
              <p>{selectedProject.budget}</p>
            </div>
            <div className="detail-section">
              <h4>Key Technologies</h4>
              <div className="tech-tags">
                {selectedProject.technologies.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTechnologies = () => (
    <div className="technologies-section">
      <h2>Advanced Technologies</h2>
      
      <div className="tech-categories">
        <div className="tech-category">
          <h3><FaSatellite /> Satellite Systems</h3>
          <ul>
            <li>Multi-band communication payloads</li>
            <li>Advanced attitude control systems</li>
            <li>Solar array deployment mechanisms</li>
            <li>Thermal management solutions</li>
            <li>Miniaturized propulsion systems</li>
          </ul>
        </div>

        <div className="tech-category">
          <h3><FaEye /> Earth Observation</h3>
          <ul>
            <li>Hyperspectral imaging sensors</li>
            <li>Synthetic Aperture Radar (SAR)</li>
            <li>Multi-spectral camera systems</li>
            <li>Real-time image processing</li>
            <li>AI-powered object detection</li>
          </ul>
        </div>

        <div className="tech-category">
          <h3><FaWifi /> Communication Systems</h3>
          <ul>
            <li>Ka-band transponders</li>
            <li>Phased array antennas</li>
            <li>Software-defined radios</li>
            <li>Quantum communication protocols</li>
            <li>Inter-satellite link technology</li>
          </ul>
        </div>

        <div className="tech-category">
          <h3><FaMapMarkedAlt /> Navigation & Positioning</h3>
          <ul>
            <li>Precision GPS augmentation</li>
            <li>Autonomous navigation systems</li>
            <li>Orbital debris tracking</li>
            <li>Formation flying algorithms</li>
            <li>Deep space navigation</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-tech-page py-5 my-5">
      <div className="page-header">
        <button onClick={onBack} className="back-button">
          <FaArrowLeft /> Back to Technologies
        </button>
        <div className="header-content">
          <div className="header-icon">
            <FaSatelliteDish />
          </div>
          <div className="header-text ">
            <h1 className='text-center'>Space Technologies Division</h1>
            <p className='text-center' >Pioneering the next generation of space exploration and satellite systems</p>
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaGlobe /> Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            <FaRocket /> Projects

          </button>
          <button 
            className={`tab-button ${activeTab === 'technologies' ? 'active' : ''}`}
            onClick={() => setActiveTab('technologies')}
          >
            <FaCog /> Technologies
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'projects' && renderProjects()}
          {activeTab === 'technologies' && renderTechnologies()}
        </div>
      </div>

      <style jsx>{`

        .space-tech-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0a1f 0%, #1a1a3e 50%, #0f0f23 100%);
          color: white;
          font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        .space-tech-page::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(236, 72, 153, 0.05) 0%, transparent 50%);
          pointer-events: none;
        }

        .page-header {

          background: rgba(15, 23, 42, 0.8);
          backdrop-filter: blur(20px);
          padding: 2rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
          position: relative;
          z-index: 1;

        }

        .back-button {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1));
          border: 1px solid rgba(139, 92, 246, 0.3);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-weight: 500;
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.1);
        }

        .back-button:hover {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(139, 92, 246, 0.2));
          transform: translateX(-3px) translateY(-1px);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.5);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 3rem;
        }

        .header-icon {
          font-size: 5rem;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.3));
          animation: pulse 3s ease-in-out infinite alternate;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          100% { transform: scale(1.05); }
        }

        .header-text h1 {
          font-size: 3.5rem;
          margin: 0;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .header-text p {
          font-size: 1.3rem;
          margin: 1rem 0 0 0;
          opacity: 0.9;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 400;
          line-height: 1.6;
        }

        .content-container {
          padding: 3rem 2rem;
          position: relative;
          z-index: 1;
        }

        .tab-navigation {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 3rem;
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 0.5rem;
          border: 1px solid rgba(139, 92, 246, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .tab-button {
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          padding: 1rem 2rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-radius: 12px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-weight: 500;
          font-size: 1rem;
          position: relative;
          overflow: hidden;
        }

        .tab-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(6, 182, 212, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .tab-button:hover {
          color: white;
          transform: translateY(-2px);
        }

        .tab-button:hover::before {
          opacity: 1;
        }

        .tab-button.active {

          color: white;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(6, 182, 212, 0.3));
          box-shadow: 0 4px 20px rgba(139, 92, 246, 0.2);
          transform: translateY(-1px);

        }

        .tab-button.active::before {
          opacity: 0;
        }

        .company-intro {

          text-align: center;
          margin-bottom: 4rem;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;

        }

        .company-intro h2 {

          font-size: 3rem;
          margin-bottom: 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }

        .intro-text {
          font-size: 1.2rem;
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 400;
        }

        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-bottom: 4rem;

        }

        .achievement-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(6, 182, 212, 0.05));
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 20px;
          padding: 2.5rem;
          text-align: center;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .achievement-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(6, 182, 212, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .achievement-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.5);
        }

        .achievement-card:hover::before {
          opacity: 1;
        }

        .achievement-metric {
          font-size: 3.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.75rem;
          position: relative;
          z-index: 1;
        }

        .achievement-label {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
          position: relative;
          z-index: 1;
        }

        .capabilities-section h3 {
          font-size: 2.5rem;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 3rem;
          text-align: center;
          font-weight: 700;
        }

        .capabilities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2.5rem;
        }

        .capability-card {
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.05));
          border: 1px solid rgba(6, 182, 212, 0.3);
          border-radius: 20px;
          padding: 2.5rem;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .capability-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .capability-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(6, 182, 212, 0.15);
          border-color: rgba(6, 182, 212, 0.5);
        }

        .capability-card:hover::before {
          opacity: 1;
        }

        .capability-icon {
          font-size: 3.5rem;
          background: linear-gradient(135deg, #06b6d4, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .capability-card h4 {
          font-size: 1.4rem;
          margin-bottom: 1rem;
          color: #06b6d4;
          font-weight: 600;
          position: relative;
          z-index: 1;
        }

        .capability-card p {
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.8);
          position: relative;
          z-index: 1;
        }

        .projects-section h2 {
          font-size: 3rem;
          background: linear-gradient(135deg,rgb(0, 0, 0), #06b6d4,rgb(139, 139, 139));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 3rem;
          text-align: center;
          font-weight: 700;
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1rem;
          margin-bottom: 0.3rem;
        }

        .project-card {
          background: linear-gradient(135deg, rgb(138, 92, 246), rgb(6, 181, 212));
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 20px;
          padding: 2.5rem;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .project-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgb(122, 102, 170), rgb(37, 98, 108));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .project-card:hover, .project-card.selected {
          border-color: rgba(255, 255, 255, 0.6);
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(157, 115, 254, 0.88);
        }

        .project-card:hover::before, .project-card.selected::before {
          opacity: 1;
        }

        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .project-header h3 {
          font-size: 1.6rem;
          background: linear-gradient(135deg, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          font-weight: 600;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 25px;
          font-size: 0.85rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .status-badge.active-development {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1));
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.4);
        }

        .status-badge.research-phase {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1));
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.4);
        }

        .status-badge.conceptual {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1));
          color: #8b5cf6;
          border: 1px solid rgba(139, 92, 246, 0.4);
        }

        .status-badge.operational {
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(6, 182, 212, 0.1));
          color: #06b6d4;
          border: 1px solid rgba(6, 182, 212, 0.4);
        }

        .project-description {
          line-height: 1.7;
          margin-bottom: 1.5rem;
          color: rgba(255, 255, 255, 0.9);
          position: relative;
          z-index: 1;
        }

        .project-meta {
          display: flex;
          gap: 2rem;
          position: relative;
          z-index: 1;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .project-details {
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(139, 92, 246, 0.1));
          border: 1px solid rgba(6, 182, 212, 0.4);
          border-radius: 20px;
          padding: 2.5rem;
          margin-top: 3rem;
          backdrop-filter: blur(15px);
          box-shadow: 0 20px 40px rgba(6, 182, 212, 0.1);
        }

        .project-details h3 {
          background: linear-gradient(135deg, #06b6d4, #8b5cf6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 2rem;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .detail-section h4 {
          color: #06b6d4;
          margin-bottom: 1rem;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .detail-section p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.1rem;
        }

        .tech-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .tech-tag {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(6, 182, 212, 0.2));
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          border: 1px solid rgba(139, 92, 246, 0.4);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .tech-tag:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.3);
        } 0.5rem;
        }

        .tech-tag {
          background: rgba(139, 92, 246, 0.3);
          color: #8b5cf6;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.8rem;
          border: 1px solid #8b5cf6;
        }

        .technologies-section h2 {
          font-size: 2.5rem;
          color: #8b5cf6;
          margin-bottom: 2rem;
          text-align: center;
        }

        .tech-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
        }

        .tech-category {
          background: rgba(6, 182, 212, 0.1);
          border: 1px solid rgba(6, 182, 212, 0.3);
          border-radius: 1rem;
          padding: 2rem;
        }

        .tech-category h3 {
          color: #06b6d4;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .tech-category ul {
          list-style: none;
          padding: 0;
        }

        .tech-category li {
          padding: 0.5rem 0;
          border-bottom: 1px solid rgba(6, 182, 212, 0.2);
          position: relative;
          padding-left: 1.5rem;
        }

        .tech-category li:before {
          content: "▶";
          position: absolute;
          left: 0;
          color: #06b6d4;
        }

        .tech-category li:last-child {
          border-bottom: none;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            text-align: center;
          }

          .header-text h1 {
            font-size: 2rem;
          }

          .tab-navigation {
            flex-direction: column;
          }

          .projects-grid,
          .capabilities-grid,
          .tech-categories {
            grid-template-columns: 1fr;
          }

          .achievements-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default SpaceTechnologiesPage;